import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, LogOut, Users, Building2, Mail, Download } from "lucide-react";
import { api } from "../lib/api";

const TABS = [
  { key: "all", label: "All" },
  { key: "waitlist", label: "Waitlist" },
  { key: "business", label: "Business" },
  { key: "oem", label: "OEM Partners" },
  { key: "press", label: "Press" },
  { key: "newsletter", label: "Newsletter" },
];

const STATUSES = ["New", "Contacted", "Qualified"];
const STATUS_STYLE = {
  New: "border-white/20 text-revo-ice",
  Contacted: "border-amber-400/40 text-amber-300",
  Qualified: "border-revo-green/50 text-revo-green",
};

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = () => { localStorage.removeItem("revo_token"); navigate("/admin/login"); };

  const changeStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/admin/leads/${id}/status`, { status });
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export", { params: { lead_type: tab }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `chargerevo-leads-${tab}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("revo_token")) { navigate("/admin/login"); return; }
    (async () => {
      try {
        const s = await api.get("/admin/stats");
        setStats(s.data);
      } catch (e) {
        if (e.response?.status === 401) logout();
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/leads`, { params: { lead_type: tab } });
        setLeads(data);
      } catch (e) {
        if (e.response?.status === 401) logout();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [tab]);

  return (
    <div className="min-h-screen bg-navy px-5 py-8 md:px-8">
      <div className="mx-auto max-w-6xl">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-revo-green"><Zap className="h-4 w-4 text-navy" fill="currentColor" /></span>
            <span className="font-display text-xl font-extrabold text-white">ChargeRevo Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportCsv} data-testid="admin-export-csv-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-revo-ice transition-colors hover:border-revo-green hover:text-revo-green">
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-revo-ice transition-colors hover:text-white">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </header>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {[["Total", stats.total, Users], ["Waitlist", stats.waitlist, Users], ["Business", stats.business, Building2], ["OEM", stats.oem, Building2], ["Press", stats.press, Mail], ["Newsletter", stats.newsletter, Mail]].map(([l, v, Icon]) => (
            <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <Icon className="h-5 w-5 text-revo-green" />
              <p className="mt-3 font-display text-3xl font-black text-white" data-testid={`stat-${l.toLowerCase()}`}>{v || 0}</p>
              <p className="text-xs uppercase tracking-wider text-revo-ice/50">{l}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 inline-flex rounded-full border border-white/15 bg-white/5 p-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-revo-green text-navy" : "text-revo-ice hover:text-white"}`}>{t.label}</button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10" data-testid="admin-leads-table">
          {loading ? (
            <div className="p-10 text-center text-revo-ice/50">Loading…</div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-revo-ice/50">No submissions yet.</div>
          ) : (
            <div className="divide-y divide-white/10">
              {leads.map((l) => (
                <div key={l.id} className="flex flex-col gap-2 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="admin-lead-row">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-revo-green/15 px-2.5 py-0.5 text-xs font-bold text-revo-green">{l.type}</span>
                      <span className="font-display font-bold text-white">{l.data.name || l.data.company || l.data.email}</span>
                    </div>
                    <p className="mt-1 text-sm text-revo-ice/70">{l.data.email}</p>
                    <p className="mt-1 text-xs text-revo-ice/50">
                      {l.data.interest && `Interest: ${l.data.interest} · `}
                      {l.data.devices != null && `Devices: ${l.data.devices} · `}
                      {l.data.zip_code && `ZIP: ${l.data.zip_code} · `}
                      {l.data.annual_savings != null && `Est. savings: $${Math.round(l.data.annual_savings).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                    <select
                      value={l.status || "New"}
                      onChange={(e) => changeStatus(l.id, e.target.value)}
                      data-testid={`lead-status-select-${l.id}`}
                      className={`rounded-full border bg-navy px-3 py-1.5 text-xs font-semibold focus:outline-none ${STATUS_STYLE[l.status || "New"]}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-navy text-white">{s}</option>
                      ))}
                    </select>
                    <span className="text-xs text-revo-ice/40">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
